import React from 'react';
import { Box, Divider, SelectChangeEvent, Stack } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Order, OrderStatus } from '@api/Orders/Order';
import { SalesChannel } from '@api/SalesChannels/SalesChannel';
import { TableKiosk } from '@api/TablesKiosks/TableKiosk';
import { ShoppingCartLineV2, ShoppingCartV2 } from '@api/ShoppingCart/ShoppingCartV2';

import { CartLineList } from '@screens/POS/components/CartLineList';
import CashFlowCalculator from '@screens/POS/components/CashFlowCalculator';
import ExpenseRegister from '@screens/POS/components/ExpenseRegister';
import { OrderTopBar } from '@screens/POS/components/OrderTopBar';
import { OrderTotals } from '@screens/POS/components/OrderTotals';
import { OrderActionsBar } from '@screens/POS/components/OrderActionsBar';
import { OrderChannelSelect } from '@screens/POS/components/OrderChannelSelect';
import { OrderCustomerForm } from '@screens/POS/components/OrderCustomerForm';
import { OrderActionButtons } from '@screens/POS/components/OrderActionButtons';
import { TableCurrentOrders } from '@screens/POS/components/TableCurrentOrders';

/**
 * El PANEL DERECHO del POS — las 3 zonas de F1 (dos fijas + el carrito que
 * scrollea), sacado del render de `POSScreen` (F4 del plan, §7.0).
 *
 * Es PRESENTACIONAL: no tiene estado ni sabe de modales ni de servicios; todo
 * llega ya calculado. Cada prop se llama IGUAL que en el componente hijo que lo
 * consume, para que la conexión se lea nombre-por-nombre — hay muchos
 * `() => void` y muchos `number`, y TypeScript no puede avisar si se cruzaran
 * dos del mismo tipo.
 */
interface PosOrderPanelProps {
    // --- Barra de arriba ---
    onToggleSavedOrders: () => void;
    onToggleCashFlow: () => void;
    onToggleExpenses: () => void;
    onSaveOrder: () => void;
    onClean: () => void;
    onShowTicket: () => void;
    showCashFlowCalculator: boolean;
    showExpenseRegister: boolean;

    // --- Contexto del pedido ---
    shoppingCart?: ShoppingCartV2;
    currentOrder?: Order;
    isEditing: boolean;
    selectedTable?: TableKiosk;

    // --- Carrito ---
    onEditLine: (line: ShoppingCartLineV2) => void;
    onRemoveLine: (index: number) => void;

    // --- Las 4 acciones del pedido (D3) ---
    onToggleGetCustomer: () => void;
    onToggleAddNote: () => void;
    onTogglePromo: () => void;
    onAddShipping: () => void;

    // --- Resumen ---
    getSubtotal: () => number;
    getPromotion: () => number;
    getTotal: () => number;
    getTotalCash: () => number;
    rewardAmount: number;
    tipAmount: number;
    creditCardAmount: number;
    bankTransferAmount: number;
    cashRegisteredAmount: number;
    onRemoveCreditCard: () => void;
    onRemoveBankTransfer: () => void;
    onRemoveCashRegistered: () => void;
    onRemoveTip: () => void;
    onRemovePoints: () => void;
    onRemovePromo: () => void;
    onRemoveShipping: () => void;

    // --- Cliente, puntos y nota ---
    onRemoveCustomer: () => void;
    onCustomerNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    setRewardAmount: (amount: number) => void;
    getPointsList: (points: number) => any[];
    onEditNote: () => void;
    onRemoveNote: () => void;

    // --- Canal/mesa y cobro ---
    salesChannels: SalesChannel[];
    tables: TableKiosk[];
    selectedOption: string;
    onSelectOptionChange: (event: SelectChangeEvent<string>) => void;
    waitingForPayment: boolean;
    setWaitingForPayment: (waiting: boolean) => void;
    onConfirmCreate: () => void;
    onConfirmComplete: () => void;
    onConfirmUpdate: () => void;
}

export const PosOrderPanel: React.FunctionComponent<PosOrderPanelProps> = (props) => {
    const {
        onToggleSavedOrders,
        onToggleCashFlow,
        onToggleExpenses,
        onSaveOrder,
        onClean,
        onShowTicket,
        showCashFlowCalculator,
        showExpenseRegister,
        shoppingCart,
        currentOrder,
        isEditing,
        selectedTable,
        onEditLine,
        onRemoveLine,
        onToggleGetCustomer,
        onToggleAddNote,
        onTogglePromo,
        onAddShipping,
        getSubtotal,
        getPromotion,
        getTotal,
        getTotalCash,
        rewardAmount,
        tipAmount,
        creditCardAmount,
        bankTransferAmount,
        cashRegisteredAmount,
        onRemoveCreditCard,
        onRemoveBankTransfer,
        onRemoveCashRegistered,
        onRemoveTip,
        onRemovePoints,
        onRemovePromo,
        onRemoveShipping,
        onRemoveCustomer,
        onCustomerNameChange,
        setRewardAmount,
        getPointsList,
        onEditNote,
        onRemoveNote,
        salesChannels,
        tables,
        selectedOption,
        onSelectOptionChange,
        waitingForPayment,
        setWaitingForPayment,
        onConfirmCreate,
        onConfirmComplete,
        onConfirmUpdate
    } = props;

    return (
        <Grid
            item
            xs={12}
            md={5}
            // El alto fijo solo aplica cuando las columnas van LADO A LADO:
            // abajo de `md` se apilan y dos paneles al 100% darian scroll
            // de pagina (ahi el contenido manda, como antes).
            sx={{
                height: { xs: 'auto', md: '100%' },
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <OrderTopBar
                onToggleSavedOrders={onToggleSavedOrders}
                onToggleCashFlow={onToggleCashFlow}
                onToggleExpenses={onToggleExpenses}
                onSaveOrder={onSaveOrder}
                onClean={onClean}
                onShowTicket={onShowTicket}
                shoppingCart={shoppingCart}
                showCashFlowCalculator={showCashFlowCalculator}
                showExpenseRegister={showExpenseRegister}
            />
            <Divider />
            {showExpenseRegister ? (
                <ExpenseRegister />
            ) : showCashFlowCalculator ? (
                <CashFlowCalculator />
            ) : (
                <Stack flex={1} minHeight={0} p={{ xs: 2, md: 3 }} spacing={2}>
                    {/* ZONA FIJA: cuenta de la mesa (D8 — misma condicion de siempre) */}
                    {currentOrder?.status !== OrderStatus.WaitingForPayment && !isEditing && (
                        <TableCurrentOrders tableId={selectedTable?.id} tableName={selectedTable?.name} />
                    )}

                    <CartLineList shoppingCart={shoppingCart} onEditLine={onEditLine} onRemoveLine={onRemoveLine} />

                    {/* ZONA FIJA: resumen + cliente + cobro. Sin `flex={1}`:
                        lo tenia y competia 50/50 con el carrito, que es el
                        unico que debe crecer y scrollear. */}
                    <Stack spacing={1.5} flexShrink={0}>
                        {/* D3: las 4 acciones del pedido, juntas y siempre
                            visibles (dos de ellas se iban con el scroll del
                            carrito y dos se escondían sin cliente) */}
                        <OrderActionsBar
                            shoppingCart={shoppingCart}
                            onToggleGetCustomer={onToggleGetCustomer}
                            onToggleAddNote={onToggleAddNote}
                            onTogglePromo={onTogglePromo}
                            onAddShipping={onAddShipping}
                        />

                        <OrderTotals
                            getSubtotal={getSubtotal}
                            getPromotion={getPromotion}
                            getTotal={getTotal}
                            getTotalCash={getTotalCash}
                            shoppingCart={shoppingCart}
                            rewardAmount={rewardAmount}
                            tipAmount={tipAmount}
                            creditCardAmount={creditCardAmount}
                            bankTransferAmount={bankTransferAmount}
                            cashRegisteredAmount={cashRegisteredAmount}
                            onRemoveCreditCard={onRemoveCreditCard}
                            onRemoveBankTransfer={onRemoveBankTransfer}
                            onRemoveCashRegistered={onRemoveCashRegistered}
                            onRemoveTip={onRemoveTip}
                            onRemovePoints={onRemovePoints}
                            onRemovePromo={onRemovePromo}
                            onRemoveShipping={onRemoveShipping}
                        />

                        <OrderCustomerForm
                            shoppingCart={shoppingCart}
                            onRemoveCustomer={onRemoveCustomer}
                            onCustomerNameChange={onCustomerNameChange}
                            rewardAmount={rewardAmount}
                            setRewardAmount={setRewardAmount}
                            getPointsList={getPointsList}
                            onEditNote={onEditNote}
                            onRemoveNote={onRemoveNote}
                        />

                        {/* El canal/mesa abre la fila de cobro (ajuste de
                            Irving): es lo último que se define antes de
                            cerrar la venta y aquí ocupa menos alto */}
                        <Stack direction="row" spacing={1}>
                            <OrderChannelSelect
                                salesChannels={salesChannels}
                                tables={tables}
                                selectedOption={selectedOption}
                                onSelectOptionChange={onSelectOptionChange}
                            />
                            <Box flex={1} minWidth={0}>
                                <OrderActionButtons
                                    isEditing={isEditing}
                                    shoppingCart={shoppingCart}
                                    waitingForPayment={waitingForPayment}
                                    setWaitingForPayment={setWaitingForPayment}
                                    currentOrder={currentOrder}
                                    onConfirmCreate={onConfirmCreate}
                                    onConfirmComplete={onConfirmComplete}
                                    onConfirmUpdate={onConfirmUpdate}
                                />
                            </Box>
                        </Stack>
                    </Stack>
                </Stack>
            )}
        </Grid>
    );
};
